import cn from 'classnames';
import { ClientEvents } from 'common/events/ClientEvents';
import { StorageKeys } from 'common/storage/constants';
import { assertDateString } from 'common/UrlDate';
import { useEffect, useMemo, useState } from 'react';
import { DishFirstFoodItemFragment, usePreCheckoutUpsellSuggestionsLazyQuery } from 'src/gqlReactTypings.generated.d';
import { DishFirstModalComponent } from 'src/pages/consumer/dish-first/explore/components/Segments/DishFirstModal/DishFirstModal';
import { PrimaryButton } from 'src/shared/design-system/Buttons';
import { Modal } from 'src/shared/design-system/Modal';
import { useTracker } from 'src/shared/hooks/useTracker';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { useCachedZipCode } from 'src/user-preferences/useCachedZipCode';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { DietaryTags } from 'web-common/src/shared/Enums';
import { useSelector } from '../../../../../../store';
import { PreCheckoutUpsellModalBody } from './PreCheckoutUpsellModalBody';
import { usePreCheckoutUpsellModalContext } from './PreCheckoutUpsellModalContext';
import { PreCheckoutUpsellModalHeader } from './PreCheckoutUpsellModalHeader';

export const PreCheckoutUpsellModal = () => {
  const tracker = useTracker();
  const { isSmallScreen } = useScreenContext();
  const isMultiCart = useSelector((state) => state.cart.isMultiCart);

  const { isShowing, hide, foodItemIds, goToCheckout, deliveryDate } = usePreCheckoutUpsellModalContext();
  const [zipCode] = useCachedZipCode();
  const [cachedDietaryPreferences] = useLocalStorage<DietaryTags[]>(StorageKeys.DIETARY_PREFERENCES, []);

  const [query, { data, loading, error, called }] = usePreCheckoutUpsellSuggestionsLazyQuery();
  useEffect(() => {
    if (isShowing && zipCode && foodItemIds?.length) {
      query({
        variables: {
          cartFoodItemIds: foodItemIds,
          deliveryDate,
          zipCode,
          dietaryFilters: cachedDietaryPreferences,
        },
      });
    }
  }, [isShowing, query, deliveryDate, foodItemIds, zipCode, cachedDietaryPreferences]);

  // state to keep track of current "window" into suggestions (progresses as users ask for new suggestions)
  const [selectedSuggestion, setSelectedSuggestion] = useState<DishFirstFoodItemFragment | undefined>();
  const [startingSuggestionIndex, setStartingSuggestionIndex] = useState(0);
  const endingSuggestionIndex = startingSuggestionIndex + 3;
  const allSuggestions = useMemo(() => data?.preCheckoutOrderUpsellRecommendations || [], [data]);
  const suggestions = useMemo(
    () => allSuggestions.slice(startingSuggestionIndex, endingSuggestionIndex),
    [allSuggestions, startingSuggestionIndex, endingSuggestionIndex]
  );
  const resetSuggestions = () => setStartingSuggestionIndex(0);
  const showNextSuggestions = () => {
    const newIndex = startingSuggestionIndex + 3;
    setStartingSuggestionIndex(newIndex);

    const newPageNum = newIndex / 3 + 1;
    const hasMoreSuggestions = allSuggestions.slice(newIndex).length > 0;
    tracker.track(ClientEvents.PRE_CHECKOUT_UPSELL_REFRESH_SUGGESTIONS, {
      page: newPageNum,
      hasExhaustedSuggestions: !hasMoreSuggestions,
    });
  };

  // redirect in case of error or no results
  // no results should only happen for new shefs or food items OR if a user has no mains in their cart
  useEffect(() => {
    const hasLoadedNoResults = isShowing && !loading && called && allSuggestions.length === 0;
    if (hasLoadedNoResults || error) goToCheckout();
  }, [isShowing, loading, allSuggestions, goToCheckout, error, called]);

  if (!isMultiCart) {
    return null;
  }
  return (
    <>
      <Modal
        size='lg'
        className={cn('max-h-full overflow-hidden !p-0', { 'self-end !rounded-t-3xl': isSmallScreen })}
        isShowing={isShowing && !selectedSuggestion}
        onClose={() => {
          hide(true);
          resetSuggestions();
        }}
        isFullWidth={isSmallScreen}
        showCloseButton>
        <div className='h-full overflow-y-auto py-4'>
          <PreCheckoutUpsellModalHeader />

          <PreCheckoutUpsellModalBody
            deliveryDate={deliveryDate}
            showNextSuggestions={showNextSuggestions}
            onSuggestionClick={(suggestion) => setSelectedSuggestion(suggestion)}
            suggestions={suggestions}
            loading={loading}
            hide={hide}
          />

          <div className='mx-4'>
            <PrimaryButton className='w-full !rounded-lg' onClick={goToCheckout}>
              Continue to checkout
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      {selectedSuggestion && (
        <DishFirstModalComponent
          key={selectedSuggestion.foodItemId}
          foodItem={selectedSuggestion}
          deliveryDate={assertDateString(deliveryDate)}
          isShowing
          onClose={() => setSelectedSuggestion(undefined)}
          isEditingOrder={false}
        />
      )}
    </>
  );
};
